import React, { useEffect, useMemo, useState } from 'react';
import { Clapperboard } from 'lucide-react';
import { getCrewCatalog, type CrewRoleDef } from '../../services/studioService';
import AgentSeat from './AgentSeat';

/**
 * DIRECTOR'S CHAIR (W3d) — hiring your director + composer, inside the Studio where the decision belongs.
 *
 * Why this exists: the proper hiring UI (roles, director STYLES, plain-English hints) already existed as
 * `MediaCrewPicker`, but only inside NewChatPicker — which the first-run redesign removed from the path. So the
 * one creative decision that most changes the output had become unreachable for new creators. This puts it in
 * the studio as a compact, always-visible statement of WHO holds each lead role, with a way to change them.
 *
 * EACH ROLE IS A SEAT (owner law 2026-08-16: one component for every agent pick). The role used to expand into a
 * list of style rows hired straight from the label — a name and a hint is not a hiring process. Now the seat says
 * who holds it, and opens the shared hiring room where each candidate is met properly (portrait, gallery, bio)
 * before one deliberate Recruit. Several roles = several seats, never one picker with a scrollable bench.
 *
 * The craft stays hidden: a creator picks a director's style ("gritty realism", "kinetic short-form"), never a
 * model. Each flavour maps to a real catalog agent whose KB carries that craft — so the choice measurably
 * changes the output rather than being cosmetic.
 */
const ROLE_NOUN: Record<string, string> = { director: 'director', composer: 'composer' };

const DirectorChair: React.FC<{
  /** Hire this agent onto the production's crew — owned by the Studio (it creates the crew chat on the first
   *  recruit). Rejects with a human message when the crew is full or the plan blocks a new chat. */
  onRecruit: (agentId: string, replacesAgentId?: string) => Promise<void>;
  /** Take the holder of a lead role off the production; the crew chat follows (owner 2026-09-13). */
  onDismiss?: (agentId: string) => Promise<void>;
  /** Catalog profile ids already on the crew — the current holder of each role, if any. */
  memberProfileIds: string[];
  /** Called after a hire so the parent can refresh the roster. */
  onHired?: () => void;
}> = ({ onRecruit, onDismiss, memberProfileIds, onHired }) => {
  const [roles, setRoles] = useState<CrewRoleDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    getCrewCatalog()
      .then((c) => { if (alive) setRoles(c.roles || []); })
      .catch(() => { if (alive) setError('Could not load the crew.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const present = useMemo(() => new Set(memberProfileIds), [memberProfileIds]);

  // Only the two roles whose craft benches we actually ship (director + composer). The rest of the crew is
  // handled by CrewStrip; this control is about the creative leads a creator deliberately CHOOSES.
  const leadRoles = useMemo(
    () => roles.filter((r) => r.role === 'director' || r.role === 'composer'),
    [roles],
  );

  const hire = async (agentId: string) => {
    await onRecruit(agentId);   // a refusal propagates to the seat, which says why
    onHired?.();
  };

  if (loading) {
    return <p className="px-0.5 py-1 text-[11px] text-secondary-400">Loading the crew…</p>;
  }
  if (!leadRoles.length) {
    return error ? <p className="px-0.5 py-1 text-[11px] text-secondary-500 dark:text-secondary-400">{error}</p> : null;
  }

  return (
    <div className="mb-4 rounded-xl border border-secondary-200 p-3.5 dark:border-secondary-800">
      <div className="mb-3 flex items-center gap-2">
        <Clapperboard className="h-4 w-4 text-secondary-700 dark:text-secondary-300" />
        <span className="text-xs font-semibold text-secondary-900 dark:text-white">Who is directing</span>
      </div>

      <div className="space-y-3">
        {leadRoles.map((r) => {
          const noun = ROLE_NOUN[r.role] || r.label.toLowerCase();
          const hired = r.flavors.find((f) => present.has(f.agentId));
          return (
            <AgentSeat
              key={r.role}
              testId={`crew-seat-${r.role}`}
              label={`Your ${noun}`}
              seatTitle={`Choose your ${noun}`}
              hiredLabel={`Your ${noun}`}
              verb="Recruit"
              candidates={r.flavors.map((f) => ({ agentId: f.agentId, name: f.name, flavor: f.flavor, hint: f.hint }))}
              currentId={hired?.agentId || null}
              onRecruit={hire}
              onDismiss={onDismiss}
            />
          );
        })}
      </div>

      {error && <p className="mt-2 px-0.5 text-[11px] text-secondary-500 dark:text-secondary-400">{error}</p>}
    </div>
  );
};

export default DirectorChair;
